import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtPKR } from "@/lib/format";

const BET_STATUS = { active: "Active", cashed_out: "Cashed Out", lost: "Lost", refunded: "Refunded" };

export const BetPanel = ({ state, multiplier, reload }) => {
  const { user, setUser } = useAuth();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const rnd = state?.round;
  const bet = state?.my_bet;
  const min = state?.min_bet || 0;
  const max = state?.max_bet || 0;

  const refreshUser = (balance) => setUser({ ...user, balance });
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
    reload();
  };

  const join = () => act(async () => {
    const val = Number(amount);
    if (!val || val < min) { toast.error(`Minimum participation is ${fmtPKR(min)}.`); return; }
    if (val > max) { toast.error(`Maximum participation is ${fmtPKR(max)}.`); return; }
    await api.post("/aviator/bet", { amount: val });
    refreshUser((state.balance || 0) - val);
    toast.success(`Joined round #${rnd.round_no} with ${fmtPKR(val)}`);
  });

  const cashout = () => act(async () => {
    const { data } = await api.post("/aviator/cashout");
    refreshUser((state.balance || 0) + data.payout);
    toast.success(`Cashed out at ${data.cashout_multiplier.toFixed(2)}x — ${fmtPKR(data.payout)}`);
  });

  const canCash = rnd?.status === "running" && bet?.status === "active";
  const canJoin = rnd?.status === "waiting" && !bet;
  const quick = [["Min", min], ["½", Math.max(min, Number(amount || 0) / 2)], ["2x", Math.min(max, Number(amount || 0) * 2 || min)], ["Max", max]];

  return (
    <div className="ec-card p-6" data-testid="aviator-bet-panel">
      <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
        <span data-testid="aviator-limits">Min {fmtPKR(min)} · Max {fmtPKR(max)}</span>
        <span data-testid="aviator-balance">Balance {fmtPKR(state?.balance)}</span>
      </div>
      <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Amount (${fmtPKR(min)} – ${fmtPKR(max)})`}
        disabled={!canJoin || busy} className="h-12 text-lg" data-testid="aviator-amount-input" />
      <div className="grid grid-cols-4 gap-2 mt-2">
        {quick.map(([l, v]) => (
          <button key={l} type="button" disabled={!canJoin} onClick={() => setAmount(String(Math.round(v)))}
            className="text-xs py-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-40 transition-colors" data-testid={`aviator-quick-${l}`}>{l}</button>
        ))}
      </div>
      {canCash ? (
        <Button onClick={cashout} disabled={busy} className="w-full h-14 mt-4 bg-yellow-500 hover:bg-yellow-400 text-black font-head font-extrabold text-lg btn-glow" data-testid="aviator-cashout-button">
          CASH OUT {fmtPKR(bet.amount * multiplier)}
        </Button>
      ) : (
        <Button onClick={join} disabled={!canJoin || busy} className="w-full h-14 mt-4 bg-orange-600 hover:bg-orange-500 font-head font-extrabold text-lg btn-glow disabled:opacity-40" data-testid="aviator-join-button">
          {!rnd ? "Game Closed" : bet ? "Joined" : rnd.status === "waiting" ? "JOIN ROUND" : "Wait for next round"}
        </Button>
      )}
      {bet && (
        <div className="mt-4 text-sm flex items-center justify-between bg-white/5 rounded-xl px-4 py-3" data-testid="aviator-my-bet">
          <span className="text-gray-400">Your entry: <span className="text-white font-semibold">{fmtPKR(bet.amount)}</span></span>
          <span className={bet.status === "cashed_out" ? "text-emerald-400 font-bold" : bet.status === "lost" ? "text-red-400 font-bold" : "text-yellow-400 font-bold"} data-testid="aviator-my-bet-status">
            {BET_STATUS[bet.status]}{bet.status === "cashed_out" ? ` @ ${bet.cashout_multiplier.toFixed(2)}x (+${fmtPKR(bet.payout)})` : ""}
          </span>
        </div>
      )}
    </div>
  );
};
